import { IconButton } from "$components/button/icon-button";
import { ChevronUpIcon } from "$components/icons";
import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps, ReactNode } from "react";
import { InvoiceCardSection, useInvoiceCardLayout } from "./invoice-card-shell";

const HeaderRow = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

const MetaColumn = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  minWidth: 0,
}));

const MetaLine = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 500,
  fontSize: 13,
  letterSpacing: "0.01em",
  color: theme.palette.text.secondary,
}));

const AmountRow = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  gap: theme.spacing(1.25),
}));

const Amount = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 700,
  fontSize: 28,
  letterSpacing: "-0.01em",
  color: theme.palette.text.primary,
}));

const AmountNote = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 500,
  fontSize: 13,
  color: theme.palette.text.secondary,
}));

const StatusCluster = styled("div")<Theme, { "data-icon-padding": number }>(
  ({ theme, ...props }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    // Reached through the class rather than by styling IconButton/SvgIcon
    // directly: both are function components, and MUI v4's styled() can emit a
    // rule-less class when it wraps one.
    "& .MuiIconButton-root": { padding: props["data-icon-padding"] },
    "& .MuiSvgIcon-root": { fontSize: 18 },
  }),
);

export const InvoiceCardHeader: FC<
  {
    dateLabel: string;
    encounterTypeLabel: string;
    clinicianName?: string;
    amount: string;
    note?: ReactNode;
    chip: ReactNode;
    onCollapse?: () => void;
    collapseLabel?: string;
  } & Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({
  dateLabel,
  encounterTypeLabel,
  clinicianName,
  amount,
  note,
  chip,
  onCollapse,
  collapseLabel = "Collapse",
  ...restProps
}) => {
  const { isCompact, iconButtonPadding } = useInvoiceCardLayout();

  const meta = [
    dateLabel,
    encounterTypeLabel,
    isCompact ? undefined : clinicianName,
  ].filter(Boolean);

  return (
    <InvoiceCardSection paddingTop={16} paddingBottom={14} {...restProps}>
      <HeaderRow>
        <MetaColumn>
          <MetaLine>{meta.join(" · ")}</MetaLine>
          <AmountRow>
            <Amount>{amount}</Amount>
            {note && <AmountNote>{note}</AmountNote>}
          </AmountRow>
        </MetaColumn>
        <StatusCluster data-icon-padding={iconButtonPadding}>
          {chip}
          <IconButton
            color="primary"
            size="small"
            aria-label={collapseLabel}
            onClick={onCollapse}
          >
            <ChevronUpIcon />
          </IconButton>
        </StatusCluster>
      </HeaderRow>
    </InvoiceCardSection>
  );
};
